import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, Save, X, Utensils, Wine, BedDouble } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api";
import ImageUploadField from "../../components/ImageUploadField";

const EMPTY = {
  museum_id: "", type: "restaurant", name: "", description: "", image_url: "",
  address: "", lat: null, lng: null, phone: "", website: "", price_range: "",
  sponsored: false, active: true,
};

const TYPE_META = {
  restaurant: { icon: Utensils, label: "Ristorante", color: "#E11D48" },
  bar: { icon: Wine, label: "Bar", color: "#7C3AED" },
  hotel: { icon: BedDouble, label: "Hotel", color: "#0891B2" },
};

export default function Places() {
  const [items, setItems] = useState([]);
  const [museums, setMuseums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        api.adminListPlaces(filter || undefined),
        api.adminListMuseums(),
      ]);
      setItems(p.items || []); setMuseums(m.items || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  async function save() {
    const body = { ...editing };
    if (body.lat === "") body.lat = null; else if (body.lat) body.lat = Number(body.lat);
    if (body.lng === "") body.lng = null; else if (body.lng) body.lng = Number(body.lng);
    try {
      if (editing.id) await api.adminUpdatePlace(editing.id, body);
      else await api.adminCreatePlace(body);
      toast.success("Salvato"); setEditing(null); load();
    } catch (e) { toast.error("Salvataggio fallito", { description: e.message }); }
  }

  async function remove(id) {
    if (!window.confirm("Eliminare?")) return;
    try { await api.adminDeletePlace(id); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(e.message); }
  }

  return (
    <div className="p-8 page-enter">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-4xl text-ink">Luoghi</h1>
          <p className="text-sm text-ink-soft mt-1">Ristoranti, bar e hotel · {items.length} totali</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 bg-white border border-border rounded-xl text-sm">
            <option value="">Tutti i musei</option>
            {museums.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button onClick={() => setEditing({ ...EMPTY, museum_id: filter || museums[0]?.id || "" })} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-5 py-2.5 rounded-full font-semibold transition">
            <Plus size={18} /> Nuovo luogo
          </button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => {
            const meta = TYPE_META[p.type] || TYPE_META.restaurant;
            const Icon = meta.icon;
            return (
              <div key={p.id} className="bg-white border border-border rounded-2xl p-4 flex gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white shrink-0" style={{ backgroundColor: meta.color }}>
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-ink truncate">{p.name}</div>
                  <div className="text-xs text-ink-soft truncate">{p.address}</div>
                  <div className="text-[11px] text-brand mt-1">{museums.find((m) => m.id === p.museum_id)?.name}</div>
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => setEditing({ ...p })} className="w-8 h-8 rounded-lg bg-bg hover:bg-brand/10 hover:text-brand flex items-center justify-center transition"><Edit2 size={14} /></button>
                  <button onClick={() => remove(p.id)} className="w-8 h-8 rounded-lg bg-bg hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition"><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
          {items.length === 0 && <div className="col-span-2 text-center py-10 text-sm text-ink-soft">Nessun luogo</div>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white">
              <h2 className="font-heading font-bold text-2xl text-ink">{editing.id ? "Modifica" : "Nuovo"} Luogo</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-bg"><X /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <Field label="Museo" full>
                <select value={editing.museum_id} onChange={(e) => setEditing({ ...editing, museum_id: e.target.value })} className="form-input">
                  {museums.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </Field>
              <Field label="Tipo">
                <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="form-input">
                  <option value="restaurant">Ristorante</option>
                  <option value="bar">Bar</option>
                  <option value="hotel">Hotel</option>
                </select>
              </Field>
              <Field label="Range prezzo">
                <input value={editing.price_range || ""} onChange={(e) => setEditing({ ...editing, price_range: e.target.value })} placeholder="€€" className="form-input" />
              </Field>
              <Field label="Nome" full>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="form-input" />
              </Field>
              <Field label="Indirizzo" full>
                <input value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} className="form-input" />
              </Field>
              <Field label="Telefono">
                <input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="form-input" />
              </Field>
              <Field label="Website">
                <input value={editing.website || ""} onChange={(e) => setEditing({ ...editing, website: e.target.value })} className="form-input" />
              </Field>
              <Field label="Latitudine">
                <input type="number" step="any" value={editing.lat ?? ""} onChange={(e) => setEditing({ ...editing, lat: e.target.value })} className="form-input" />
              </Field>
              <Field label="Longitudine">
                <input type="number" step="any" value={editing.lng ?? ""} onChange={(e) => setEditing({ ...editing, lng: e.target.value })} className="form-input" />
              </Field>
              <Field label="Foto" full>
                <ImageUploadField
                  value={editing.image_url || ""}
                  onChange={(v) => setEditing({ ...editing, image_url: v })}
                  testId="place-image-upload"
                />
              </Field>
              <Field label="Descrizione" full>
                <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="form-input resize-none" />
              </Field>
              <div className="col-span-2 flex gap-4">
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                  Attivo
                </label>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={editing.sponsored} onChange={(e) => setEditing({ ...editing, sponsored: e.target.checked })} />
                  Sponsorizzato
                </label>
              </div>
            </div>
            <div className="flex gap-2 justify-end px-6 py-4 border-t border-border sticky bottom-0 bg-white">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-xl bg-bg text-ink-soft font-medium hover:bg-border transition">Annulla</button>
              <button onClick={save} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-5 py-2 rounded-xl font-semibold transition"><Save size={16} /> Salva</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.form-input{width:100%;padding:.65rem .9rem;border:1px solid hsl(var(--border));border-radius:.7rem;background:#fff;font-size:.875rem;color:#1c1c1e;outline:none;transition:.15s}.form-input:focus{border-color:#0B4A99}`}</style>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[11px] tracking-widest font-semibold text-ink-soft block mb-1.5">{label.toUpperCase()}</label>
      {children}
    </div>
  );
}
